/**
 * User identity — one person per email (docs/superpowers/specs/2026-09-30-user-identity-hygiene-design.md).
 *
 * Pure: no React, no services, nothing touching window at import time, so
 * scripts/test/userIdentity.test.mjs can run it under plain Node.
 *
 * Email is the identity AND the Cognito username, and Cognito usernames are
 * always lowercase. Every email the client sends is normalized here first.
 */

/** Trimmed + lowercased; '' for blank or non-string input. */
export function normalizeEmail(value) {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

/**
 * The post-sign-in profile lookup. Encoded (a '+' otherwise arrives as a space) but
 * NOT lowercased: Cognito already resolved the identity, and lowercasing could miss a
 * mixed-case users.Email row if the server compares case-sensitively.
 */
export function authLoginPath(email) {
  return `/auth/login/?email=${encodeURIComponent(email)}`;
}

/**
 * Lowercase an <input>'s value as the user types, keeping the caret where it was.
 * For uncontrolled inputs (the Amplify login form). type="email" inputs throw on
 * setSelectionRange — the value is still lowercased, the caret just isn't restored.
 */
export function lowercaseInPlace(input) {
  if (!input || typeof input.value !== 'string') return;
  const lower = input.value.toLowerCase();
  if (lower === input.value) return;
  const { selectionStart, selectionEnd } = input;
  input.value = lower;
  if (selectionStart == null || typeof input.setSelectionRange !== 'function') return;
  try {
    input.setSelectionRange(selectionStart, selectionEnd);
  } catch (e) {
    // type="email" does not support selection APIs; the lowercase value is what matters.
  }
}

/**
 * The existing person from a `409 { code: 'USER_EXISTS', existingUser }`, else null.
 * Any other 409 (e.g. POST /auth/create-user's Cognito conflict) is NOT a user
 * conflict and must fall through to the caller's normal error handling.
 */
export function readUserConflict(error) {
  const res = error && error.response;
  if (!res || res.status !== 409) return null;
  const body = res.data || {};
  const u = body.existingUser;
  if (body.code !== 'USER_EXISTS' || !u || u.userId == null) return null;
  return {
    userId: u.userId,
    name: typeof u.name === 'string' ? u.name : '',
    email: normalizeEmail(u.email),
    hasLogin: u.hasLogin === true,
  };
}

/** Provision a login after linking only if one was asked for and none exists. */
export function needsLoginAfterLink(existingUser, wantsLogin) {
  return wantsLogin === true && !(existingUser && existingUser.hasLogin === true);
}

function displayName(existingUser) {
  return existingUser.name ? `${existingUser.name} (${existingUser.email})` : existingUser.email;
}

export function linkPromptMessage(existingUser, companyLabel, roleLabel) {
  return `${displayName(existingUser)} already has a Jobvious account. Add them to ${companyLabel} as ${roleLabel}?`;
}

export function linkResultMessage(result, existingUser, companyLabel) {
  const who = existingUser.name || existingUser.email;
  if (result && result.alreadyLinked) {
    return `${who} is already part of ${companyLabel}. Nothing was changed.`;
  }
  return `${who} was added to ${companyLabel}.`;
}
